import { useState } from 'react';
import { IDonorProfileHeaderInfo } from '@nkr/models/donor-profile';
import { Alert, Button, Glyphicon } from 'react-bootstrap';
import { HeaderWrapper, styles } from './styles';
import { ToggleType } from './models';
import { Icon } from 'react-icons-kit';
import { ic_brightness_1 } from 'react-icons-kit/md/ic_brightness_1';

interface IProps {
    data: IDonorProfileHeaderInfo,
    centerId: number,
    toggleType: ToggleType,
    messages: string[],
    activeKey: string,
    cheekSwabNotification: boolean,
    cheekOrderDataNotify: () => void,
    hideCheekOrderDataNotify: () => void,
    onTabChange: (href: string) => void
}

const DonorProfileHeaderInfo = (props: IProps) => {
    const [expand, setExpand] = useState(false);

    const renderDivider = () => {
        return <div className='item-divider'><Icon size={10} icon={ic_brightness_1} /></div>;
    };

    const renderOrderInfo = () => {
        if (props.data.lastOrderDate) {
            if (props.data.isRequisition) {
                return <div>An on-hand kit was used and a requisition was generated on {props.data.lastOrderDate}</div>
            } else {
                return <div>A cheek swab was last ordered {props.data.lastOrderDate}</div>
            }
        }

        return null;
    }

    if (props.data == null) {
        return null;
    }

    return (
        <div style={{ marginBottom: 20 }}>
            {props.toggleType == ToggleType.Success && (
                <Alert bsStyle='success' bsSize='sm'>
                    Save completed successfully
                </Alert>
            )}

            {props.cheekSwabNotification && (
                <Alert bsStyle='success'>
                    <div style={{ display: 'flow-root' }}>
                        <span style={{ float: 'left' }}>
                            <strong>Please click the 'Donor Actions' button, in order to do the following: </strong>
                            <ul>
                                <li>
                                    Order High Res Tissue Typing (Cheek Swab)
                                </li>
                                <li>
                                    Utilize a Bulk Cheek Swab (Kit on hand)
                                </li>
                            </ul>
                        </span>
                        <span style={{ float: 'right' }}>
                            <Button onClick={props.hideCheekOrderDataNotify}>
                                X
                            </Button>
                        </span>
                    </div>
                </Alert>
            )}

            {props.toggleType == ToggleType.Errors && props.messages.length > 0 && (
                <Alert bsStyle='danger' bsSize='sm'>
                    The donor record was NOT saved. Please fix the following errors and try again:
                    <ul>
                        {props.messages.map(message => <li>{message}</li>)}
                    </ul>
                </Alert>
            )}

            {props.data.clusterId != 0 && props.data.chainId != 0 && (
                <Alert bsStyle='warning' bsSize='sm' style={{ marginBottom: 5 }}>
                    <span>
                        <strong> Chain: </strong> {props.data.chainId} ({props.data.chainStatus})
                        <strong> Cluster: </strong> {props.data.clusterNumber} ({props.data.clusterStatus})
                        <strong> Position: </strong> {props.data.positionInSwap}
                        {props.data.isTransplanted && (
                            <strong> {`(Transplanted)`}</strong>
                        )}
                    </span>
                </Alert>
            )}

            {!props.data.isTransplanted && (
                <HeaderWrapper className='well well-sm'>
                    <div className='donor-progress-bar'>
                        <div className='header-left' onClick={() => setExpand(!expand)}>
                            <Button block>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <div style={{ marginRight: 10, fontSize: 16, fontWeight: 'bold' }}>{props.data.alias}</div>
                                    <div>
                                        <div>{props.data.isExportable ? <>Matchable</> : <>Will Not Be Matched</>}</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(click to expand)</div>
                                    </div>
                                </div>
                            </Button>
                        </div>

                        <div style={{ paddingLeft: 0, paddingRight: 15 }}><Glyphicon glyph='chevron-right' /></div>

                        <div className='header-buttons'>
                            <div className='item-button'>
                                {!props.data.lastOrderDate && !props.data.alleleData &&
                                    <Button block bsStyle='danger' onClick={props.cheekOrderDataNotify}>
                                        <div>ORDER CHEEK SWAB KIT</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(No cheek swab ordered for this donor)</div>
                                    </Button>
                                }
                                {props.data.lastOrderDate && !props.data.alleleData &&
                                    <Button block bsStyle='warning' onClick={props.cheekOrderDataNotify}>
                                        <div>ORDER CHEEK SWAB KIT</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(A cheek swab was last ordered {props.data.lastOrderDate})</div>
                                    </Button>
                                }
                                {props.data.alleleData &&
                                    <Button block bsStyle='success' onClick={props.cheekOrderDataNotify}>
                                        <div>ORDER CHEEK SWAB KIT</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(Received results)</div>
                                    </Button>
                                }
                            </div>

                            {renderDivider()}

                            <div className='item-button'>
                                {props.data.isExportable && props.data.validationResults.length == 0 ?
                                    <Button block bsStyle='success' onClick={() => setExpand(!expand)}>
                                        <div>Required Fields for Exportability</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(all requirements have been met)</div>
                                    </Button>
                                    :
                                    <Button block bsStyle='danger' onClick={() => setExpand(!expand)}>
                                        <div>Required Fields for Exportability</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>({props.data.validationResults.length > 0 ? props.data.validationResults.length + ' issue(s) found' : 'not flagged for matching - click to expand'})</div>
                                    </Button>
                                }
                            </div>

                            {props.data.isAdp && (
                                <>
                                    {renderDivider()}

                                    <div className='item-button'>
                                        {props.data.hasAdpFile ?
                                            <Button block bsStyle='success' onClick={() => props.activeKey != 'DONOR-DATA' ? props.onTabChange('/center/' + props.centerId + '/donor/' + props.data.id + '/profile/donor-data') : null}>
                                                <div>Upload Voucher Consent</div>
                                                <div style={{ fontSize: 12, lineHeight: '12px' }}>(consent form is on file{props.activeKey != 'DONOR-DATA' ? ' - click to go to Donor Data tab' : ''})</div>
                                            </Button>
                                            :
                                            <Button block bsStyle='danger' onClick={() => props.activeKey != 'DONOR-DATA' ? props.onTabChange('/center/' + props.centerId + '/donor/' + props.data.id + '/profile/donor-data') : null}>
                                                <div>Upload Voucher Consent</div>
                                                <div style={{ fontSize: 12, lineHeight: '12px' }}>(consent form not uploaded{props.activeKey != 'DONOR-DATA' ? ' - click to go to Donor Data tab' : ''})</div>
                                            </Button>
                                        }
                                    </div>
                                </>
                            )}

                            {renderDivider()}

                            <div className='item-button'>
                                {props.data.isExportable && props.data.validationResults.length == 0 ?
                                    <Button block bsStyle='success'>
                                        <div>Complete</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(ready for matching)</div>
                                    </Button>
                                    :
                                    <Button block bsStyle='danger'>
                                        <div>Incomplete</div>
                                        <div style={{ fontSize: 12, lineHeight: '12px' }}>(not ready for matching)</div>
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>

                    {expand && (
                        <div className='donor-progress-messages'>
                            <Alert bsSize='sm' style={{ marginBottom: 5 }}>
                                <h4>CHEEK SWAB KIT ORDER STATUS</h4>
                                {renderOrderInfo()}
                            </Alert>

                            {props.data.validationResults.length > 0 && (
                                <Alert bsSize='sm' style={{ marginBottom: 5 }}>
                                    <h4>Required Fields for Exportability</h4>
                                    <div style={{ marginBottom: 5 }}>This donor will not be included in match runs for the following reasons:</div>
                                    <ul style={{ paddingInlineStart: 20 }}>
                                        {props.data.validationResults.map((item, index) =>
                                            <li style={{ listStyleType: 'disc' }} key={'vad-' + index}>{item}</li>
                                        )}
                                    </ul>
                                </Alert>
                            )}

                            {!props.data.isExportable && props.data.validationResults.length == 0 && (
                                <Alert bsSize='sm' style={styles.progressMessage}>
                                    <h4>Not Ready for Matching</h4>
                                    <div style={styles.progressMessageIntro}>This donor has not been flagged as exportable by the matching system, but no specific issue was found. Possible things to check:</div>
                                    <ul style={styles.progressMessageList}>
                                        <li style={styles.progressMessageListItem}>Save the donor record. Some changes, such as uploading the voucher consent or changes to the paired recipient, are not picked up until the donor is saved.</li>
                                        <li style={styles.progressMessageListItem}>Infectious disease results (Anti-HCV, HIV, EBV, CMV, Hepatitis B Antigen, Hepatitis B Antibody, RPR/VDRL) must be positive or negative.</li>
                                        <li style={styles.progressMessageListItem}>Directed donors must have a paired recipient linked, and that recipient's required fields must be complete.</li>
                                    </ul>
                                </Alert>
                            )}
                        </div>
                    )}
                </HeaderWrapper>
            )}
        </div>
    );
}

export default DonorProfileHeaderInfo;